// Code generated by @hex-insights/frontend-generator; While this comment is present this file may be overwritten. If manual edits are made, remove this comment.
import React from "react";
import * as Apollo from "@apollo/client";
import { IndexForms } from "@hex-insights/app-modules";
import { RequiredKeys, TypeOrArray } from "@hex-insights/core";
import { FormState } from "@hex-insights/forms";
import { Table } from "@hex-insights/tables";
import {
	NewsletterFilterInput,
	NewsletterIndexQuery,
	NewsletterIndexQueryVariables,
	NewsletterOrderField,
	useNewsletterIndexQuery,
} from "../../../Utilities";
import { NewsletterFilterFormState, NewsletterFilterFormUtils, NewsletterFilterFormValues } from "../../Filter";
import { NewsletterOrderFormState, NewsletterOrderFormValues } from "../../Order";
import { DefaultNode, GenericNewsletterIndexQuery } from "./types";

function defaultOrderFieldToColumn(field: NewsletterOrderField | null) {
	if (field === null) {
		return "id";
	}
	return NewsletterOrderFormValues.orderFieldToModelField[field];
}

function defaultColumnToOrderField(column: string) {
	if (column === "id") {
		return null;
	}
	return NewsletterOrderFormValues.modelFieldToOrderField[column];
}

export type UseIndexArgs<T, V extends Record<string, any>> = {
	useFilterFormState?: () => FormState<NewsletterFilterFormValues.FilterFormValues>;
	useOrderFormState?: () => FormState<NewsletterOrderFormValues.OrderFormValues>;
	usePaginationFormState?: () => FormState<IndexForms.PaginationFormValues>;
	transformQueryVariables?: (
		queryVariables: IndexForms.QueryVariables<TypeOrArray<NewsletterFilterInput>, NewsletterOrderField | null>,
	) => V;
	useQuery?: (options: Apollo.QueryHookOptions<GenericNewsletterIndexQuery<T>, V>) => {
		loading: boolean;
		data?: GenericNewsletterIndexQuery<T>;
		error?: Apollo.ApolloError | null;
	};
	orderFieldToColumn?: (field: NewsletterOrderField | null) => string;
	columnToOrderField?: (column: string) => NewsletterOrderField | null;
	queryOptions?: Omit<Apollo.QueryHookOptions<NewsletterIndexQuery, V>, "variables">;
};

export function useIndex(
	args?: Omit<UseIndexArgs<any, any>, "useQuery"> & { useQuery?: undefined },
): UseIndexResult<DefaultNode>;
export function useIndex<T>(
	args: RequiredKeys<UseIndexArgs<T, NewsletterIndexQueryVariables>, "useQuery">,
): UseIndexResult<T>;
export function useIndex<T, V extends Record<string, any>>(
	args: RequiredKeys<UseIndexArgs<T, V>, "useQuery" | "transformQueryVariables">,
): UseIndexResult<T>;
export function useIndex<T>({
	useFilterFormState = NewsletterFilterFormState.useFilterFormStateWithQueryStateSync,
	useOrderFormState = NewsletterOrderFormState.useOrderFormStateWithQueryStateSync,
	usePaginationFormState = IndexForms.usePaginationFormStateWithQueryStateAndLocalStorageSync,
	transformQueryVariables,
	useQuery = useNewsletterIndexQuery,
	orderFieldToColumn = defaultOrderFieldToColumn,
	columnToOrderField = defaultColumnToOrderField,
	queryOptions,
}: UseIndexArgs<any, any> = {}): UseIndexResult<T> {
	const filterFormState = useFilterFormState();
	const orderFormState = useOrderFormState();
	const paginationFormState = usePaginationFormState();

	const filterInputs = IndexForms.useFilterInput(filterFormState.formValues, NewsletterFilterFormUtils.toFilterInputs);
	const orderInput = IndexForms.useOrderInput(orderFormState.formValues);
	const rawQueryVariables = IndexForms.useQueryVariables(
		filterInputs,
		orderInput,
		paginationFormState.formValues,
		paginationFormState.formSetFunctions.cursor,
	);
	const queryVariables = transformQueryVariables
		? React.useMemo(() => transformQueryVariables(rawQueryVariables), [transformQueryVariables, rawQueryVariables])
		: rawQueryVariables;
	const { loading, data, error } = useQuery({ ...queryOptions, variables: queryVariables });

	const clearFilters = React.useCallback(() => {
		NewsletterFilterFormUtils.clearFilters(filterFormState);
	}, [filterFormState]);

	const numActiveFilters = NewsletterFilterFormUtils.countActiveFilters(filterFormState.formValues);
	const [paginationInfo, setPaginationInfo] = IndexForms.usePaginationInfo();

	const { scrollSyncID, synchronizeScroll } = Table.useScrollSync();
	const tableSortingProps = IndexForms.useTableSortingFromOrderForm(
		orderFormState,
		orderFieldToColumn,
		columnToOrderField,
	);

	const numFillerRows = Math.max(
		(paginationFormState.formValues.resultsPerPage ?? 0) - (data?.newsletterConnection.edges.length ?? 0),
		0,
	);

	return {
		filterFormState,
		orderFormState,
		paginationFormState,
		queryVariables,
		loading,
		data,
		error,
		clearFilters,
		numActiveFilters,
		paginationInfo,
		setPaginationInfo,
		scrollSyncID,
		synchronizeScroll,
		tableSortingProps,
		numFillerRows,
	} as UseIndexResult<T>;
}

export type UseIndexResult<T = DefaultNode> = {
	filterFormState: FormState<NewsletterFilterFormValues.FilterFormValues>;
	orderFormState: FormState<NewsletterOrderFormValues.OrderFormValues>;
	paginationFormState: FormState<IndexForms.PaginationFormValues>;
	queryVariables: IndexForms.QueryVariables<TypeOrArray<NewsletterFilterInput>, NewsletterOrderField | null>;
	loading: boolean;
	data: GenericNewsletterIndexQuery<T> | undefined;
	error: Apollo.ApolloError | undefined;
	clearFilters: () => void;
	numActiveFilters: number;
	paginationInfo: IndexForms.PaginationInfo;
	setPaginationInfo: React.Dispatch<React.SetStateAction<IndexForms.PaginationInfo>>;
	scrollSyncID: number;
	synchronizeScroll: () => void;
	tableSortingProps: IndexForms.TableSortingProps<string>;
	numFillerRows: number;
};
